/**
 * Elegir a quién es la venta — o crearlo en el momento.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTA PANTALLA TIENE QUE CREAR, Y NO SOLO BUSCAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque el momento en que hace falta un cliente es **siempre la primera vez que
 * ese cliente aparece**. Nadie da de alta a la señora del kiosco un martes por
 * la tarde "por si algún día fía": la registra el día que le fía, con ella
 * delante y otros dos esperando en la cola.
 *
 * Un selector que solo busque obliga a: cancelar la venta, ir a una pantalla de
 * clientes, crearlo, volver, rehacer el carrito. Nadie hace eso dos veces —
 * a la tercera se apunta en un cuaderno y Softfi deja de servir para fiar.
 *
 * Por eso lo que se escribe en el buscador **es** el nombre del cliente nuevo:
 * si no aparece nadie, el botón de crear ya viene con el nombre escrito.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  SOLO SE PIDE NOMBRE Y TELÉFONO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La cédula, el correo y la dirección existen en la ficha del cliente y se
 * pueden llenar después, con calma. Aquí no: cada campo de este formulario es
 * un cliente esperando de pie.
 *
 * El teléfono no es un capricho — es lo que después permite escribirle por
 * WhatsApp para cobrarle, y lo que hace que el mismo señor no acabe con tres
 * fichas distintas.
 *
 * ⚠️ Salvo lo que el negocio exija (Clientes → Opciones, 2026-10-05): eso se
 * marca con asterisco y se pide AQUÍ, antes de guardar en el aparato. El
 * servidor no lo vuelve a revisar al sincronizar —perdería al cliente de una
 * venta ya hecha—, así que este formulario es la única puerta. El correo solo
 * aparece si se exige. Ver `lib/obligatoriosCliente.ts`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTA PANTALLA NO PREGUNTA AL SERVIDOR. NUNCA.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ni para buscar ni para registrar. Lo contó el dueño después de trabajar sin
 * internet: «si tenemos la configuración de que los clientes sean obligatorios
 * está dañado porque no salen, hay que crear, y eso hace que se buguee si
 * creamos otro cliente».
 *
 * Y era exacto. El buscador preguntaba al servidor: sin señal devolvía la lista
 * vacía, así que TODOS los clientes del negocio dejaban de existir. Registrar
 * tampoco llegaba a ninguna parte. Con los clientes obligatorios eso no es una
 * molestia: es no poder cobrar.
 *
 * Ahora lee de la copia del dispositivo —que el punto de venta baja entera
 * junto al catálogo— y registrar escribe ahí primero. El envío al servidor
 * ocurre detrás, cuando se pueda, exactamente igual que con las ventas.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, UserPlus, X } from "lucide-react";
import { Boton, Campo, Etiquetado, Lista, cx, Nota } from "@/components/ui";
import { Confirmar, Modal } from "@/components/Modal";
import { mensajeDeError } from "@/lib/api";
import { clientesGuardados, guardarClienteLocal } from "@/lib/db";
import { clientes, listasDePrecios } from "@/lib/endpoints";
import type { Customer } from "@/lib/types";
import { aviso } from "@/stores/ui";
import { useAuth } from "@/stores/auth";
import { useOffline } from "@/stores/offline";
import { buscarClientes, clienteConEseDocumento, ordenarClientes } from "./buscarClientes";
import { revisarClientesDesdeElSelector } from "./clientesDelAparato";
import {
  type DatoDeCliente,
  faltanEnCliente,
  mensajeDeDatosFaltantes,
  useDatosExigidos,
} from "@/lib/obligatoriosCliente";
import { VerMas } from "@/components/VerMas";
import { nombreConMayusculas } from "@/lib/texto";

/** Cuántas filas se pintan de entrada; el resto, con «Ver más». */
const POR_TANDA = 30;

export function SelectorDeCliente({
  abierto,
  cerrar,
  alElegir,
  motivo,
}: {
  abierto: boolean;
  cerrar: () => void;
  alElegir: (cliente: Customer) => void;
  /** Por qué se está pidiendo. Cambia el texto de arriba, no el comportamiento. */
  motivo?: "fiar" | "venta" | "cotizacion" | "cuenta";
}) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState("");
  const [creando, setCreando] = useState(false);
  const [telefono, setTelefono] = useState("");
  const [cedula, setCedula] = useState("");
  const [tipoDoc, setTipoDoc] = useState("");
  const [direccion, setDireccion] = useState("");
  const [correo, setCorreo] = useState("");

  // Lo que exige el negocio. Sale de los ajustes guardados en el aparato, así
  // que sin internet sigue sabiendo qué pedir.
  const exigidos = useDatosExigidos();
  const exige = (d: DatoDeCliente) => exigidos.includes(d);
  const [quisoGuardar, setQuisoGuardar] = useState(false);

  /*
    ⚠️ EL NOMBRE ES SU PROPIO ESTADO, Y ESE ERA EL FALLO.

    Antes el nombre se sacaba del buscador: `nombreLimpio = texto.trim()`. Así
    que quien buscaba por cédula —que es lo normal, es el dato que la gente se
    sabe de memoria— y pulsaba «Registrar», acababa con un cliente llamado
    «30875348». Después había que borrarlo y escribir el nombre encima.

    Ahora lo que se escribió se reparte según lo que sea: si parece un
    documento va a la cédula, si no, al nombre.
  */
  const [nombre, setNombre] = useState("");

  /*
    Los documentos de ESTE país: V/E/J en Venezuela, DNI/CUIT en Argentina.

    ⚠️ Se pide una vez y no caduca. En el mostrador no puede haber una espera
    por una lista que no cambia nunca.
  */
  const { data: docs } = useQuery({
    queryKey: ["tipos-de-documento"],
    queryFn: clientes.tiposDeDocumento,
    staleTime: Infinity,
  });
  const [error, setError] = useState<string | null>(null);

  const { puede } = useAuth();
  const puedeCambiarPrecios = puede("catalog.manage");

  // Las listas activas, para poder pegarle una a un cliente sin salir de aquí.
  const { data: listasActivas } = useQuery({
    queryKey: ["listas-de-precios", "activas"],
    queryFn: () => listasDePrecios.listar(true),
    enabled: puedeCambiarPrecios,
  });

  const ponerLista = useMutation({
    mutationFn: ({ id, lista }: { id: string; lista: string | null }) =>
      clientes.actualizar(id, { lista_precios_id: lista }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clientes"] });
      aviso.exito("Listo", "A partir de ahora se le cobra con esos precios.");
    },
    onError: (e) => setError(mensajeDeError(e)),
  });

  // Al abrir, la copia del aparato se pone al día: un Excel importado o un
  // cliente registrado en otra caja tienen que aparecer sin cerrar sesión.
  // Si nada cambió, solo viajan las huellas. Ver `clientesDelAparato.ts`.
  useEffect(() => {
    if (abierto) revisarClientesDesdeElSelector();
  }, [abierto]);

  useEffect(() => {
    if (!abierto) {
      setTexto("");
      setCreando(false);
      setTelefono("");
      setCedula("");
      setNombre("");
      setDireccion("");
      setCorreo("");
      setQuisoGuardar(false);
      setError(null);
    }
  }, [abierto]);

  // Se espera un momento antes de buscar. Sin esto, escribir "Pedro" lanza
  // cinco búsquedas y las respuestas pueden llegar desordenadas: se ven los
  // resultados de "Ped" después de los de "Pedro".
  const [busqueda, setBusqueda] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBusqueda(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);

  /*
    ⚠️ TODOS los clientes del dispositivo, y se filtran aquí.

    Podría filtrarse en la consulta y volver a leer de la base con cada letra.
    Se lee una vez y se filtra en memoria porque son cientos de filas de tres
    campos: filtrar eso es instantáneo, y así escribir no toca el disco.

    La clave empieza por «clientes» a propósito: media aplicación invalida
    `["clientes"]` al tocar uno, y así esta lista se entera sin que haya que ir
    a buscar cada sitio.
  */
  const { data: todos, isFetching } = useQuery({
    queryKey: ["clientes", "del-dispositivo"],
    queryFn: clientesGuardados,
    enabled: abierto,
  });

  // Ordenados UNA vez, al abrir: con la copia entera pueden ser decenas de
  // miles, y ordenarlos con cada letra se notaría al teclear.
  const ordenados = useMemo(() => ordenarClientes(todos ?? []), [todos]);

  // Todas las coincidencias; se pintan de a treinta —de esta lista se elige
  // UNA persona— y el resto sale con «Ver más». Antes eran treinta y ya: con
  // muchas «María», la que se buscaba podía no aparecer nunca.
  const coincidencias = useMemo(
    () => buscarClientes(ordenados, busqueda, Number.POSITIVE_INFINITY, { yaOrdenados: true }),
    [ordenados, busqueda],
  );
  const [cuantosSeVen, setCuantosSeVen] = useState(POR_TANDA);
  useEffect(() => setCuantosSeVen(POR_TANDA), [busqueda]);
  const lista = coincidencias.slice(0, cuantosSeVen);

  // Y si lo que se busca no está en el aparato, se pregunta también: puede ser
  // alguien que se importó o se registró en otra caja hace un momento. Si
  // llega, la lista se repinta sola.
  const sinCoincidencias = abierto && busqueda !== "" && todos !== undefined && coincidencias.length === 0;
  useEffect(() => {
    if (sinCoincidencias) revisarClientesDesdeElSelector();
  }, [sinCoincidencias, busqueda]);
  const nombreLimpio = nombre.trim();

  const faltan = faltanEnCliente(exigidos, { documento: cedula, telefono, correo, direccion });
  const errorDe = (d: DatoDeCliente) =>
    quisoGuardar && faltan.includes(d) ? "Obligatorio en este negocio." : undefined;
  // Se va solo al completar lo que falta: se calcula con lo escrito.
  const avisoDeFaltan =
    quisoGuardar && faltan.length > 0 ? mensajeDeDatosFaltantes(faltan) : null;

  /**
   * Revisa lo obligatorio y registra.
   *
   * ⚠️ Un documento que ya está en el aparato no se revisa: no se registra a
   * nadie, se devuelve al cliente de siempre (ver `crear`). Pedirle el correo
   * a doña Carmen para elegirla sería cerrar la puerta a quien ya está dentro.
   */
  function intentarGuardar() {
    setError(null);
    const repetido = clienteConEseDocumento(todos ?? [], cedula);
    if (!repetido && faltan.length > 0) {
      setQuisoGuardar(true);
      return;
    }
    crear.mutate();
  }

  /**
   * ¿Lo que se escribió parece un documento y no un nombre?
   *
   * Se mira que sea casi todo dígitos: una cédula venezolana son 7-9, un RIF
   * lleva letra delante y puntos, y nadie se llama «30875348».
   *
   * ⚠️ Se exige un mínimo de cinco para no confundir con alguien que empezó a
   * teclear un nombre corto — «Ana» no puede acabar en la casilla de la cédula.
   */
  function pareceDocumento(valor: string): boolean {
    const limpio = valor.trim();
    if (limpio.length < 5) return false;
    const digitos = limpio.replace(/\D/g, "");
    return digitos.length >= 5 && digitos.length >= limpio.replace(/[\s.\-]/g, "").length - 1;
  }

  /**
   * Abre el formulario repartiendo lo que ya se escribió.
   *
   * Es la diferencia entre «registrar» y «registrar y ahora corrige lo que
   * puse mal»: quien buscó una cédula la encuentra ya en su casilla, con el
   * cursor puesto en el nombre, que es lo único que falta.
   */
  function abrirFormulario() {
    const escrito = texto.trim();
    if (pareceDocumento(escrito)) {
      setCedula(escrito);
      setNombre("");
    } else {
      setNombre(escrito);
    }
    setCreando(true);
  }

  const crear = useMutation({
    mutationFn: async (): Promise<Customer> => {
      /*
        ═══════════════════════════════════════════════════════════════════════
         ⚠️ LA CÉDULA REPETIDA SE ATAJA AQUÍ, PORQUE SIN SEÑAL NO HAY QUIÉN
        ═══════════════════════════════════════════════════════════════════════

        Con internet lo hacía el servidor: registrar una cédula que ya existe
        devuelve al cliente de siempre en vez de crear una segunda ficha. Sin
        internet no hay quién lo compruebe, y el cajero —que tampoco pudo
        buscarla— crearía la ficha duplicada de doña Carmen con su deuda
        repartida entre las dos.

        Se devuelve el que ya está, igual que hace el servidor. No es un error
        que haya que enseñar: es que ese cliente ya estaba registrado.
      */
      const repetido = clienteConEseDocumento(todos ?? [], cedula);
      if (repetido) return repetido;

      /*
        ⚠️ EL ID LO ELIGE ESTE APARATO, Y EL SERVIDOR LO RESPETA.

        Es lo que permite que la venta que se va a hacer ahora mismo apunte a
        este cliente aunque él todavía no exista allá. Ver `client_uuid` en
        `CustomerIn`, del lado del servidor.
      */
      const cliente: Customer = {
        id: crypto.randomUUID(),
        // Con mayúscula inicial, como lo guarda el servidor: sin internet el
        // recibo sale ahora mismo con este nombre. Ver `nombreConMayusculas`.
        name: nombreConMayusculas(nombreLimpio),
        phone: telefono.trim() || null,
        email: correo.trim() || null,
        tax_id_tipo: tipoDoc || null,
        tax_id: cedula.trim() || null,
        address: direccion.trim() || null,
        notes: null,
        is_active: true,
        lista_precios_id: null,
        lista_precios_nombre: null,
      };

      await guardarClienteLocal(cliente);
      return cliente;
    },
    onSuccess: (cliente) => {
      qc.invalidateQueries({ queryKey: ["clientes"] });
      aviso.exito(`${cliente.name} quedó registrado`);
      alElegir(cliente);
      cerrar();

      /*
        Se manda al servidor por detrás, sin esperarlo.

        A quien está cobrando no le sirve de nada esperar a que llegue: el
        cliente ya se puede elegir, la venta ya puede seguir. Y si no hay señal,
        se queda en la cola y sale con las ventas.
      */
      void useOffline.getState().sincronizar({ silencioso: true });
    },
    onError: (e) => setError(mensajeDeError(e)),
  });

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ CERRAR A MEDIO REGISTRAR UN CLIENTE BORRABA TODO SIN AVISAR
    ═══════════════════════════════════════════════════════════════════════════

    Lo encontró el dueño: «para agregar clientes podemos cancelar eso y no
    poner cliente, y eso está mal». Y lo era — se escribía el nombre, la
    cédula, el teléfono y la dirección de alguien que está delante del
    mostrador, se pulsaba la X, y todo desaparecía. Ni se creó el cliente ni
    quedó nada: hay que volver a pedírselo todo con el cliente esperando.

    La X es fácil de tocar por error justo ahí, porque está donde siempre.

    ⚠️ Solo pregunta si hay ALGO escrito. Abrir el formulario, ver que no era
    eso y cerrar es un caso legítimo, y un «¿seguro?» sobre un formulario vacío
    es de las cosas que enseñan a pulsar «sí» sin leer.
  */
  const hayAlgoEscrito =
    creando &&
    Boolean(
      nombre.trim() || cedula.trim() || telefono.trim() || direccion.trim() || correo.trim(),
    );
  const [confirmandoSalir, setConfirmandoSalir] = useState(false);

  function intentarCerrar() {
    if (hayAlgoEscrito) {
      setConfirmandoSalir(true);
      return;
    }
    cerrar();
  }

  return (
    <>
    <Modal
      abierto={abierto}
      onCerrar={intentarCerrar}
      /*
        ⚠️ El título dice de qué documento se está hablando.

        «¿De quién es la venta?» encima de una cotización es una pregunta sobre
        algo que no está pasando: no se está vendiendo nada todavía. Y quien
        duda de qué está haciendo, cancela.
      */
      titulo={
        motivo === "fiar"
          ? "¿A quién le das el crédito?"
          : motivo === "cuenta"
            ? "¿Quién te debe?"
          : motivo === "cotizacion"
            ? "¿Para quién es la cotización?"
            : "¿De quién es la venta?"
      }
      /*
        ⚠️ ANCHO «lg», Y ANTES ERA «md».

        En «sm» el desplegable del documento y el número no caben en la misma
        fila y el selector se salía del cuadro. Eso ya estaba resuelto; lo que
        seguía mal es que esta ventana es una LISTA, no un formulario corto.

        Lo pidió el dueño: «asignar cliente hacerlo más grande». Y tiene el
        motivo a la vista en su propia captura — un cuadro a media pantalla con
        UN cliente dentro y el resto hueco. Aquí se busca entre cientos de
        personas con alguien esperando de pie: cuantas más filas quepan de un
        vistazo, menos hay que escribir.
      */
      ancho="lg"
    >
      {motivo === "fiar" && (
        <p className="mb-4 text-sm leading-relaxed text-muted">
          Una deuda tiene que tener dueño: sin esto no habría a quién cobrarle después.
        </p>
      )}

      {motivo === "cuenta" && (
        <p className="mb-4 text-sm leading-relaxed text-muted">
          La cuenta queda a su nombre y aparece en su expediente de Por cobrar, junto a sus ventas
          a crédito.
        </p>
      )}

      {motivo === "cotizacion" && (
        <p className="mb-4 text-sm leading-relaxed text-muted">
          Va impreso en el papel, y es a quien llamas después para preguntar si lo
          va a tomar.
        </p>
      )}

      {creando ? (
        /* ═══════════ CREARLO EN EL MOMENTO ═══════════ */
        <div className="space-y-4">
          <Etiquetado label="Nombre" requerido>
            <Campo
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="María González"
              autoFocus
            />
          </Etiquetado>

          {/*
            ═══════════════════════════════════════════════════════════════════
             LA CÉDULA VA PRIMERO, Y NO POR CAPRICHO
            ═══════════════════════════════════════════════════════════════════

            Es el dato que la gente se sabe de memoria y recita sin pensar. En
            una farmacia o un supermercado grande te la piden antes que el
            nombre, y por eso funciona: no hay que deletrear nada.

            Y para Softfi es lo que evita que el mismo señor acabe con tres
            fichas distintas: la cédula tiene un índice único por negocio, y se
            busca sin puntos ni guiones — «V-12.345.678» y «12345678» encuentran
            al mismo.
          */}
          {/*
            ⚠️ El tipo se ELIGE aunque estemos en el mostrador.

            Parece un paso más con cola detrás, pero no lo es: viene marcado el
            de las personas —el que se usa en casi toda venta— así que quien no
            quiera pensarlo no toca nada. Y a cambio, el recibo sale igual lo
            cargue quien lo cargue.
          */}
          <Etiquetado
            label={docs?.tipos.length ? "Documento" : "Cédula o RIF"}
            ayuda="Con esto lo encuentras en dos segundos la próxima vez."
            requerido={exige("documento")}
            error={errorDe("documento")}
          >
            <div className="flex gap-2">
              {(docs?.tipos.length ?? 0) > 0 && (
                /*
                  ⚠️ EL ANCHO VA EN EL DIV, NUNCA EN EL `<select>`.

                  Aquí ponía `w-auto` encima de la Lista, y no servía de nada:
                  todos los campos de Softfi traen `w-full` en su clase base, y
                  `cx()` solo pega textos — no decide cuál gana. Eso lo decide
                  la hoja de estilos, y ahí `w-full` iba delante.

                  Resultado: el desplegable de V/E/J se comía la fila entera y
                  empujaba el número fuera del cuadro. Por eso salía una barra
                  de desplazamiento horizontal en un formulario de cuatro
                  campos. Es el mismo fallo que ya aplastaba el monto en la
                  pantalla de cobro.

                  `w-20` da para «CUIT», que es el código más largo del
                  catálogo.
                */
                <div className="w-20 shrink-0">
                  <Lista
                    value={tipoDoc}
                    onChange={(e) => setTipoDoc(e.target.value)}
                    aria-label="Tipo de documento"
                  >
                    <option value="">—</option>
                    {docs!.tipos.map((t) => (
                      <option key={t.codigo} value={t.codigo}>
                        {t.codigo}
                      </option>
                    ))}
                  </Lista>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Campo
                  value={cedula}
                  onChange={(e) => setCedula(e.target.value)}
                  placeholder={
                    docs?.tipos.find((t) => t.codigo === tipoDoc)?.ejemplo ?? "12345678"
                  }
                  className="tabular"
                />
              </div>
            </div>
          </Etiquetado>

          <Etiquetado
            label="Teléfono"
            ayuda="Para poder escribirle por WhatsApp cuando toque cobrar."
            requerido={exige("telefono")}
            error={errorDe("telefono")}
          >
            <Campo
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="584121234567"
              inputMode="tel"
            />
          </Etiquetado>

          {/*
            ⚠️ La dirección SÍ está aquí, y es opcional.

            El argumento de «nadie dicta una dirección con cola detrás» sigue
            siendo cierto, y por eso va la última y sin marca de obligatoria —
            quien tenga prisa la salta y no pasa nada. Pero dejarla fuera
            obligaba a ir después a la ficha del cliente para algo que muchas
            veces la persona está diciendo en ese momento, sobre todo si el
            negocio lleva a domicilio.
          */}
          {/*
            El correo, solo si el negocio lo exige: con cola detrás no se
            ofrece un campo que nadie pidió. Se completa en la ficha.
          */}
          {exige("correo") && (
            <Etiquetado label="Correo" requerido error={errorDe("correo")}>
              <Campo
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                placeholder="maria@ejemplo.com"
                inputMode="email"
                maxLength={255}
              />
            </Etiquetado>
          )}

          <Etiquetado
            label="Dirección"
            ayuda={
              exige("direccion")
                ? "Para cuando haya que llevarle algo."
                : "Opcional. Para cuando haya que llevarle algo."
            }
            requerido={exige("direccion")}
            error={errorDe("direccion")}
          >
            <Campo
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Calle 5, al lado de la panadería"
              maxLength={300}
            />
          </Etiquetado>

          {(error ?? avisoDeFaltan) && <Nota tono="peligro">{error ?? avisoDeFaltan}</Nota>}

          <div className="flex gap-2">
            <Boton variante="secundario" onClick={() => setCreando(false)} className="flex-1">
              Atrás
            </Boton>
            <Boton
              onClick={intentarGuardar}
              cargando={crear.isPending}
              disabled={nombreLimpio.length < 2}
              className="flex-1"
            >
              Guardar y usar
            </Boton>
          </div>
        </div>
      ) : (
        /* ═══════════ BUSCAR ═══════════ */
        <div className="space-y-3">
          <div className="relative">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <Campo
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                /*
                  ⚠️ ENTER HACE LO QUE HARÍA LA MANO.

                  El cajero teclea la cédula con el cliente delante y ya tiene
                  la mano en el teclado. Obligarlo a soltar y buscar con el
                  ratón el único resultado de la lista es el paso que sobra.

                  · Un solo resultado → ese es. Se elige y se cierra.
                  · Ninguno → al formulario, con lo escrito ya repartido.
                  · Varios → no se adivina: elegir por él sería peor que no
                    hacer nada, porque el error no se vería hasta el recibo.
                */
                if (lista.length === 1) {
                  alElegir(lista[0]!);
                  cerrar();
                } else if (lista.length === 0 && texto.trim().length >= 2) {
                  abrirFormulario();
                }
              }}
              placeholder="Cédula, nombre o teléfono"
              className="pl-9"
              autoFocus
            />
          </div>

          {/*
            ⚠️ EL ALTO VA CON LA PANTALLA, NO FIJO EN 288 px.

            `max-h-72` dejaba ver cuatro clientes en un monitor de escritorio
            igual que en un teléfono. En el mostrador eso son cuatro filas y a
            partir de ahí a rodar la rueda, teniendo medio modal en blanco
            debajo.

            `min()` es lo que impide pasarse por el otro lado: sin él, en una
            pantalla baja la lista empujaría el botón de «Registrar un cliente
            nuevo» fuera de la vista — y ese botón es la salida cuando el
            cliente no aparece, que es justo cuando más falta hace.
          */}
          <div className="max-h-[min(60vh,26rem)] space-y-1 overflow-y-auto">
            {isFetching && lista.length === 0 ? (
              <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted">
                <Loader2 size={15} className="animate-spin" /> Buscando…
              </p>
            ) : lista.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted">
                {nombreLimpio
                  ? `Nadie se llama «${nombreLimpio}» todavía.`
                  : "Escribe la cédula, el nombre o el teléfono."}
              </p>
            ) : (
              lista.map((c) => (
                <div
                  key={c.id}
                  // `min-w-0` para que la fila pueda encogerse: sin esto, un
                  // nombre largo la estira y aparece una barra de desplazamiento
                  // horizontal en la lista, que es lo que pasaba.
                  className="flex min-w-0 flex-wrap items-center rounded-[var(--radius-control)] hover:bg-surface-2"
                >
                  <button
                    type="button"
                    onClick={() => {
                      alElegir(c);
                      cerrar();
                    }}
                    // ⚠️ En un teléfono el nombre se lleva la fila ENTERA y el
                    // desplegable baja solo. Medido en un modal de 340 px: en
                    // una sola línea al nombre le quedaban 107 px («Doña Carmen
                    // l…»), y saber a quién le estás cobrando es lo único que
                    // de verdad importa aquí.
                    //
                    // El `sm:` va después a propósito: Tailwind escribe las
                    // variantes de pantalla DESPUÉS de las clases sueltas, así
                    // que `sm:w-auto` le gana a `w-full` sin trucos.
                    className="flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left sm:w-auto sm:flex-1"
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-2 text-sm font-semibold text-ink-soft">
                      {c.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">
                        {c.name}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {[
                          c.tax_id,
                          c.phone,
                          // La lista se ve AQUÍ, en la fila. Es lo que hace que
                          // el cajero entienda por qué el precio cambió solo al
                          // elegir a este cliente.
                          c.lista_precios_nombre ? `Precio ${c.lista_precios_nombre}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </button>

                  {/*
                    ⚠️ Solo para quien puede tocar precios.

                    Decidir a qué precio se le cobra a alguien no es cosa del
                    cajero: si pudiera, podría ponerse precio de mayor a sí
                    mismo. El servidor también lo corta, esto solo evita
                    enseñar un botón que va a dar error.
                  */}
                  {/*
                    ⚠️ EL ANCHO VA EN ESTA CAJA, NO EN EL <Lista>.

                    Se intentó con `className="w-28"` encima del componente y NO
                    FUNCIONÓ: `cx` solo pega las clases una detrás de otra, no
                    resuelve cuál gana. `Lista` ya trae `w-full`, y entre dos
                    clases de ancho manda el orden del CSS de Tailwind, no el
                    orden en que se escriben. Ganaba `w-full`.

                    Resultado en pantalla: el desplegable se comía la fila
                    entera (467 px medidos) y el nombre del cliente quedaba en
                    CERO píxeles de ancho. Al vender no se veía a quién se le
                    estaba cobrando.

                    Con la caja de fuera midiendo `w-28`, el `w-full` de dentro
                    llena esos 112 px y ya está: no hay pelea de clases que
                    perder.
                  */}
                  {puedeCambiarPrecios && (listasActivas ?? []).length > 0 && (
                    <div className="w-full shrink-0 px-3 pb-2 sm:w-36 sm:px-0 sm:pb-0 sm:pr-2">
                    <Lista
                      value={c.lista_precios_id ?? ""}
                      onChange={(e) =>
                        ponerLista.mutate({ id: c.id, lista: e.target.value || null })
                      }
                      // ⚠️ `pl` y no `px`: `px-2` pisaba el hueco de la
                      // flecha (`pr-8` de `Lista`) y «Normal» salía «Norma».
                      className="h-8 pl-2.5 text-xs"
                      aria-label={`Lista de precios de ${c.name}`}
                      title={`A qué precios se le cobra a ${c.name} de ahora en adelante`}
                    >
                      <option value="">Normal</option>
                      {(listasActivas ?? []).map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.nombre}
                        </option>
                      ))}
                    </Lista>
                    </div>
                  )}
                </div>
              ))
            )}
            <VerMas
              quedan={coincidencias.length - lista.length}
              onVerMas={() => setCuantosSeVen((n) => n + POR_TANDA)}
            />
          </div>

          {/*
            El botón de crear lleva DENTRO lo que se acaba de escribir. Es lo que
            convierte "no encontré a nadie" en un callejón sin salida o en un
            paso más — y con el cliente esperando, esa diferencia lo es todo.
          */}
          <button
            type="button"
            onClick={abrirFormulario}
            className={cx(
              "flex w-full items-center gap-3 rounded-[var(--radius-control)] border border-dashed px-3 py-3 text-left",
              "border-accent/40 text-accent hover:bg-accent-soft",
            )}
          >
            <UserPlus size={17} className="shrink-0" />
            <span className="min-w-0 flex-1 text-sm font-medium">
              {texto.trim() ? (
                <>
                  Registrar a <strong className="break-words">«{texto.trim()}»</strong>
                </>
              ) : (
                "Registrar un cliente nuevo"
              )}
            </span>
          </button>
        </div>
      )}
    </Modal>

    <Confirmar
      abierto={confirmandoSalir}
      onCerrar={() => setConfirmandoSalir(false)}
      onConfirmar={() => {
        setConfirmandoSalir(false);
        cerrar();
      }}
      peligroso
      titulo="¿Descartar los datos del cliente?"
      mensaje="Escribiste datos que todavía no se guardaron. Si sales ahora hay que volver a pedírselos."
      textoConfirmar="Descartar"
    />
    </>
  );
}

/**
 * La pastilla que enseña de quién es la venta, y que deja quitarlo.
 *
 * Va siempre visible aunque no haya cliente: si solo apareciera cuando ya hay
 * uno, nadie descubriría que se puede poner. Una función que hay que adivinar
 * no existe — es la misma lección que dejó el fiado.
 */
export function PastillaDeCliente({
  cliente,
  alPulsar,
  alQuitar,
  obligatorio,
}: {
  cliente: { id: string; nombre: string } | null;
  alPulsar: () => void;
  alQuitar: () => void;
  /** Cuando la venta lleva fiado: no se puede quitar. */
  obligatorio?: boolean;
}) {
  if (!cliente) {
    return (
      /*
        ═══════════════════════════════════════════════════════════════════════
         ⚠️ ESTO ERA UNA PASTILLA GRIS Y PUNTEADA QUE DECÍA «PONER CLIENTE»
        ═══════════════════════════════════════════════════════════════════════

        Dos cosas mal, y se las dijeron al dueño de una vez: «que lo de poner
        cliente lo cambiáramos por algo más profesional, y que lo hiciéramos
        más notorio».

        · «Poner cliente» es cómo se lo explicarías a alguien, no cómo se llama.
          «Asignar cliente» es lo que dice cualquier sistema de ventas, y quien
          viene de otro lo reconoce sin leerlo dos veces.

        · Gris, punteado y en letra pequeña es el aspecto de lo desactivado.
          Puesto al lado de un total en negrita, se lee como decoración — y una
          venta sin cliente es una venta que no se puede fiar, ni buscar
          después, ni cobrarle a nadie con lista de precios.

        Ahora lleva el color de acento y letra normal: sigue siendo secundario
        —no compite con «Cobrar»— pero se ve que es algo que se pulsa.

        ═══════════════════════════════════════════════════════════════════════
         ⚠️ Y DESPUÉS SE HIZO MÁS GRANDE Y CUADRADO. LO PIDIÓ EL DUEÑO OTRA VEZ
        ═══════════════════════════════════════════════════════════════════════

        «Hazlo más grande y el botón de asignar cuadrado para que sea aún más
        notorio».

        La forma redonda era el problema que quedaba: una pastilla se lee como
        una ETIQUETA —algo que informa— y no como un botón. Al lado de un total
        en negrita, el ojo la salta. Un rectángulo con las esquinas del resto de
        los controles se lee como lo que es: algo que se pulsa.

        Y el tamaño no es capricho. Este botón se toca con el cliente delante y
        a veces con el dedo: en `text-xs` con `py-1.5` medía 26 píxeles de alto,
        muy por debajo de los 44 que hace falta para acertarle en un teléfono
        sin mirar.
      */
      <button
        type="button"
        onClick={alPulsar}
        className={cx(
          "flex items-center gap-2 rounded-[var(--radius-control)] border px-3.5 py-2.5",
          "border-accent/50 bg-accent-soft text-sm font-semibold text-accent",
          "transition-colors hover:border-accent hover:brightness-95",
        )}
      >
        <UserPlus size={16} /> Asignar cliente
      </button>
    );
  }

  /*
    ⚠️ MISMA FORMA Y MISMO ALTO QUE EL BOTÓN DE ARRIBA.

    Los dos ocupan el mismo sitio y se turnan: si uno fuera una pastilla
    pequeña y el otro un rectángulo grande, elegir un cliente movería de golpe
    todo lo que tiene al lado —el subtotal, los impuestos— justo cuando el
    cajero está mirando esos números.
  */
  return (
    <span className="flex items-center gap-2 rounded-[var(--radius-control)] border border-accent/40 bg-accent-soft py-2 pl-3.5 pr-2 text-sm font-medium text-accent">
      <button type="button" onClick={alPulsar} className="max-w-[12rem] truncate">
        {cliente.nombre}
      </button>
      {!obligatorio && (
        <button
          type="button"
          onClick={alQuitar}
          aria-label="Quitar el cliente de esta venta"
          className="flex size-5 shrink-0 items-center justify-center rounded-[var(--radius-control)] hover:bg-accent/20"
        >
          <X size={13} />
        </button>
      )}
    </span>
  );
}
